import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ActionMenu, type ActionMenuEntry } from "@/components/admin/action-menu";
import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Segmented } from "@/components/admin/segmented";
import { Tag } from "@/components/admin/tag";
import { useConfirm, useToast } from "@/components/ui/feedback";
import { Modal, ModalFooter } from "@/components/ui/modal";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { useSegments } from "@/lib/customers/customers-hooks";
import {
	BLOCK_LABELS,
	BLOCK_TYPES,
	type Campaign,
	type NewsletterBlock,
	type NewsletterBlockType,
	NEWSLETTER_STATUS_LABELS,
	type NewsletterStatus,
	newslettersApi,
} from "@/lib/marketing/newsletters-api";

const CLE = ["marketing", "newsletters"] as const;

const nombre = (n: number) => n.toLocaleString("fr-FR");

const dateHeure = (iso: string | null) =>
	iso
		? new Date(iso).toLocaleString("fr-FR", {
				weekday: "short",
				day: "2-digit",
				month: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			})
		: "—";

type Filtre = "all" | NewsletterStatus;

/**
 * Campagnes d'emailing.
 *
 * L'audience est figée au moment de la planification, pas à l'envoi : deux
 * relances auraient sinon visé deux populations différentes, et le rapport
 * n'aurait plus voulu dire grand-chose.
 */
export function AdminMarketingNewslettersPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { hasAny } = usePermissions();
	const peutEcrire = hasAny("MARKETING.CMS.WRITE");
	const peutEnvoyer = hasAny("MARKETING.CAMPAIGN.SEND");

	const [filtre, setFiltre] = useState<Filtre>("all");
	const [edite, setEdite] = useState<string | null>(null);
	const [creation, setCreation] = useState(false);
	const [rapport, setRapport] = useState<string | null>(null);

	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "list", filtre],
		queryFn: () =>
			newslettersApi.list({ status: filtre === "all" ? undefined : filtre }),
	});
	const { data: stats } = useQuery({
		queryKey: [...CLE, "stats"],
		queryFn: () => newslettersApi.stats(),
	});

	const rafraichir = () => {
		void qc.invalidateQueries({ queryKey: CLE });
	};
	const echec = (e: unknown) => toast.error(extractErrorMessage(e));

	const creer = useMutation({
		mutationFn: (p: { name: string; subject: string; segmentId?: string }) =>
			newslettersApi.create({
				...p,
				blocks: [
					{ type: "header", title: "Bonjour {{first_name}} 👋" },
					{ type: "text", body: "Cette semaine, on a sélectionné pour toi…" },
				],
			}),
		onSuccess: (c) => {
			toast.success("Campagne créée en brouillon");
			setCreation(false);
			setEdite(c.id);
			rafraichir();
		},
		onError: echec,
	});

	const dupliquer = useMutation({
		mutationFn: (id: string) => newslettersApi.duplicate(id),
		onSuccess: () => {
			toast.success("Campagne dupliquée");
			rafraichir();
		},
		onError: echec,
	});

	const annuler = useMutation({
		mutationFn: (id: string) => newslettersApi.cancel(id),
		onSuccess: () => {
			toast.success("Campagne déprogrammée");
			rafraichir();
		},
		onError: echec,
	});

	const valider = useMutation({
		mutationFn: ({ id, reject }: { id: string; reject: boolean }) =>
			newslettersApi.approve(id, reject),
		onSuccess: (_d, v) => {
			toast.success(v.reject ? "Envoi refusé" : "Envoi validé — la campagne part");
			rafraichir();
		},
		onError: echec,
	});

	const supprimer = useMutation({
		mutationFn: (id: string) => newslettersApi.remove(id),
		onSuccess: () => {
			toast.success("Campagne supprimée");
			rafraichir();
		},
		onError: echec,
	});

	const menu = (c: Campaign): ActionMenuEntry[] => {
		const entrees: ActionMenuEntry[] = [
			{ label: "Ouvrir l'éditeur", icon: "edit", onClick: () => setEdite(c.id) },
			{ label: "Rapport", icon: "chart", onClick: () => setRapport(c.id) },
			{
				label: "Dupliquer",
				icon: "copy",
				disabled: !peutEcrire,
				onClick: () => dupliquer.mutate(c.id),
			},
		];

		if (c.approvalRequired && c.status === "scheduled") {
			entrees.push(
				{
					label: "Valider l'envoi",
					icon: "check",
					disabled: !peutEnvoyer,
					onClick: () => valider.mutate({ id: c.id, reject: false }),
				},
				{
					label: "Refuser l'envoi",
					icon: "close",
					disabled: !peutEnvoyer,
					onClick: () => valider.mutate({ id: c.id, reject: true }),
				},
			);
		}

		if (c.status === "scheduled") {
			entrees.push({
				label: "Déprogrammer",
				icon: "clock",
				disabled: !peutEcrire,
				onClick: () => annuler.mutate(c.id),
			});
		}

		if (c.status === "draft") {
			entrees.push("sep", {
				label: "Supprimer",
				icon: "trash",
				danger: true,
				disabled: !peutEcrire,
				onClick: () => {
					void confirm({
						title: "Supprimer ce brouillon ?",
						message: `« ${c.name} » sera définitivement supprimée.`,
						confirmLabel: "Supprimer",
						tone: "danger",
					}).then((ok) => {
						if (ok) supprimer.mutate(c.id);
					});
				},
			});
		}

		return entrees;
	};

	const campagnes = data?.items ?? [];

	return (
		<>
			<PageHead
				title="Newsletters"
				sub="Composition par blocs, audience, envoi et suivi"
				actions={
					peutEcrire && (
						<AdminButton variant="primary" onClick={() => setCreation(true)}>
							<AdminIcon n="plus" s={14} />
							Nouvelle campagne
						</AdminButton>
					)
				}
			/>

			{stats && (
				<div className="kpi-grid">
					<div className="kpi ochre">
						<div className="kpi-l">Abonnés joignables</div>
						<div className="kpi-v tn">{nombre(stats.subscribers)}</div>
						<span className="kpi-delta neutral">Ayant accepté les emails</span>
					</div>
					<div className="kpi accent">
						<div className="kpi-l">Taux d'ouverture moyen</div>
						<div className="kpi-v tn">
							{stats.openRate.toFixed(1)}
							<small>%</small>
						</div>
					</div>
					<div className="kpi forest">
						<div className="kpi-l">Envoyées</div>
						<div className="kpi-v tn">{stats.sent}</div>
					</div>
					<div className="kpi">
						<div className="kpi-l">Programmées</div>
						<div className="kpi-v tn">{stats.scheduled}</div>
						<span className="kpi-delta neutral">
							{stats.draft} brouillon(s)
						</span>
					</div>
				</div>
			)}

			<div style={{ marginBottom: 14 }}>
				<Segmented
					value={filtre}
					onChange={setFiltre}
					options={[
						{ value: "all", label: "Toutes" },
						{ value: "scheduled", label: "Programmées" },
						{ value: "sent", label: "Envoyées" },
						{ value: "draft", label: "Brouillons" },
					]}
				/>
			</div>

			<Card>
				<div className="table-wrap" style={{ margin: 0 }}>
					<table className="table">
						<thead>
							<tr>
								<th>Campagne</th>
								<th>Audience</th>
								<th className="r">Destinataires</th>
								<th className="r">Ouverture</th>
								<th className="r">Clic</th>
								<th>Statut</th>
								<th />
							</tr>
						</thead>
						<tbody>
							{isLoading && (
								<tr>
									<td colSpan={7}>
										<div className="empty">Chargement…</div>
									</td>
								</tr>
							)}
							{!isLoading && campagnes.length === 0 && (
								<tr>
									<td colSpan={7}>
										<div className="empty">Aucune campagne.</div>
									</td>
								</tr>
							)}
							{campagnes.map((c) => {
								const st = NEWSLETTER_STATUS_LABELS[c.status];
								return (
									<tr key={c.id}>
										<td>
											<button
												type="button"
												onClick={() => setEdite(c.id)}
												style={{
													background: "none",
													border: "none",
													padding: 0,
													textAlign: "left",
													cursor: "pointer",
													fontFamily: "inherit",
												}}
											>
												<strong style={{ fontSize: 12.5 }}>{c.name}</strong>
											</button>
											<div className="muted" style={{ fontSize: 11 }}>
												{c.subject}
											</div>
											<div className="muted" style={{ fontSize: 10.5 }}>
												{dateHeure(c.scheduledAt)}
											</div>
										</td>
										<td className="muted">{c.segmentLabel ?? "Tous opt-in"}</td>
										<td className="r tn">{nombre(c.recipients)}</td>
										<td className="r tn">
											{c.sentCount > 0 ? `${c.openRate.toFixed(1)}%` : "—"}
										</td>
										<td className="r tn">
											{c.sentCount > 0 ? `${c.clickRate.toFixed(1)}%` : "—"}
										</td>
										<td>
											<Tag variant={st.variant}>{st.label}</Tag>
											{c.approvalRequired && c.status === "scheduled" && (
												<div
													style={{
														fontSize: 10.5,
														color: "var(--a-warning)",
														marginTop: 2,
													}}
												>
													Validation requise
												</div>
											)}
											{c.failedCount > 0 && (
												<div
													className="muted"
													style={{ fontSize: 10.5, marginTop: 2 }}
												>
													{c.failedCount} échec(s)
												</div>
											)}
										</td>
										<td className="right">
											<ActionMenu items={menu(c)} />
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</Card>

			{creation && (
				<CreationModal
					saving={creer.isPending}
					onClose={() => setCreation(false)}
					onSubmit={(p) => creer.mutate(p)}
				/>
			)}

			{edite && (
				<EditeurModal
					id={edite}
					readOnly={!peutEcrire}
					onClose={() => {
						setEdite(null);
						rafraichir();
					}}
				/>
			)}

			{rapport && (
				<RapportModal id={rapport} onClose={() => setRapport(null)} />
			)}
		</>
	);
}

function CreationModal({
	onClose,
	onSubmit,
	saving,
}: {
	onClose: () => void;
	onSubmit: (p: { name: string; subject: string; segmentId?: string }) => void;
	saving: boolean;
}) {
	const { data: segments } = useSegments();
	const [nom, setNom] = useState("");
	const [objet, setObjet] = useState("");
	const [segment, setSegment] = useState("");

	return (
		<Modal
			title="Nouvelle campagne"
			subtitle="Le contenu se compose ensuite, bloc par bloc"
			onClose={onClose}
			width={520}
		>
			<FormRow label="Nom de la campagne" helper="Usage interne" required>
				<input
					className="aform-i"
					value={nom}
					onChange={(e) => setNom(e.target.value)}
					placeholder="Hebdo bons plans · S21"
				/>
			</FormRow>
			<FormRow label="Objet de l'email" helper="Ce que le client lit en premier" required>
				<input
					className="aform-i"
					value={objet}
					onChange={(e) => setObjet(e.target.value)}
					placeholder="Tes bons plans de la semaine"
				/>
			</FormRow>
			<FormRow label="Audience">
				<select
					className="aform-i"
					value={segment}
					onChange={(e) => setSegment(e.target.value)}
				>
					<option value="">Tous les abonnés opt-in</option>
					{(segments ?? []).map((s) => (
						<option key={s.id} value={s.id}>
							{s.name}
						</option>
					))}
				</select>
			</FormRow>
			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Annuler
				</AdminButton>
				<AdminButton
					variant="primary"
					disabled={nom.trim().length < 3 || objet.trim().length < 3 || saving}
					onClick={() =>
						onSubmit({
							name: nom.trim(),
							subject: objet.trim(),
							segmentId: segment || undefined,
						})
					}
				>
					Créer la campagne
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}

/**
 * Éditeur par blocs, avec aperçu réel de l'email.
 *
 * L'aperçu est rendu par le serveur, avec le même code que l'envoi : un
 * aperçu reconstruit côté admin aurait fini par mentir, et c'est précisément
 * sur un email qu'on ne peut pas se rattraper après coup.
 */
function EditeurModal({
	id,
	onClose,
	readOnly,
}: {
	id: string;
	onClose: () => void;
	readOnly: boolean;
}) {
	const qc = useQueryClient();
	const toast = useToast();
	const confirm = useConfirm();
	const { data: segments } = useSegments();

	const { data } = useQuery({
		queryKey: [...CLE, "detail", id],
		queryFn: () => newslettersApi.detail(id),
	});

	const [blocs, setBlocs] = useState<NewsletterBlock[] | null>(null);
	const [objet, setObjet] = useState("");
	const [preheader, setPreheader] = useState("");
	const [segment, setSegment] = useState("");
	const [quand, setQuand] = useState("");
	const [emailTest, setEmailTest] = useState("");
	const [apercuGrand, setApercuGrand] = useState(false);

	useEffect(() => {
		if (data && blocs === null) {
			setBlocs(data.blocks);
			setObjet(data.subject);
			setPreheader(data.preheader ?? "");
			setSegment(data.segmentId ?? "");
		}
	}, [data, blocs]);

	const { data: apercu } = useQuery({
		queryKey: [...CLE, "preview", id, JSON.stringify(blocs), objet, preheader],
		queryFn: () =>
			newslettersApi.preview({
				blocks: blocs ?? [],
				subject: objet,
				preheader,
			}),
		enabled: blocs !== null,
	});

	const { data: estimation } = useQuery({
		queryKey: [...CLE, "estimate", segment],
		queryFn: () => newslettersApi.estimate(segment || null),
	});

	const enregistrer = useMutation({
		mutationFn: () =>
			newslettersApi.update(id, {
				subject: objet,
				preheader,
				segmentId: segment || null,
				blocks: blocs ?? [],
			}),
		onSuccess: () => {
			toast.success("Campagne enregistrée");
			void qc.invalidateQueries({ queryKey: CLE });
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const planifier = useMutation({
		mutationFn: () =>
			newslettersApi.schedule(id, quand ? new Date(quand).toISOString() : undefined),
		onSuccess: (c) => {
			toast.success(
				c.approvalRequired
					? "Campagne planifiée — en attente de validation"
					: "Campagne planifiée",
			);
			void qc.invalidateQueries({ queryKey: CLE });
			onClose();
		},
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const test = useMutation({
		mutationFn: () => newslettersApi.test(id, emailTest),
		onSuccess: (r) => toast.success(`Email de contrôle envoyé à ${r.to}`),
		onError: (e) => toast.error(extractErrorMessage(e)),
	});

	const ajouter = (type: NewsletterBlockType) => {
		const defauts: Record<NewsletterBlockType, NewsletterBlock> = {
			header: { type: "header", title: "Bonjour {{first_name}} 👋" },
			text: { type: "text", body: "" },
			products: { type: "products", title: "Notre sélection", productIds: [] },
			banner: { type: "banner", title: "Offre du moment", code: "" },
			review: { type: "review", body: "", title: "" },
			article: { type: "article", title: "", body: "" },
			button: { type: "button", title: "Voir la sélection", url: "" },
		};
		setBlocs((b) => [...(b ?? []), defauts[type]]);
	};

	const deplacer = (index: number, delta: number) => {
		setBlocs((b) => {
			if (!b) return b;
			const cible = index + delta;
			if (cible < 0 || cible >= b.length) return b;
			const copie = [...b];
			[copie[index], copie[cible]] = [copie[cible], copie[index]];
			return copie;
		});
	};

	const retirer = (index: number) =>
		setBlocs((b) => (b ?? []).filter((_, i) => i !== index));

	const majBloc = (index: number, patch: Partial<NewsletterBlock>) =>
		setBlocs((b) =>
			(b ?? []).map((bloc, i) => (i === index ? { ...bloc, ...patch } : bloc)),
		);

	if (!data || blocs === null) {
		return (
			<Modal title="Éditeur de campagne" onClose={onClose} width={1040}>
				<div className="empty">Chargement…</div>
			</Modal>
		);
	}

	const figee = data.status === "sent" || data.status === "sending";

	return (
		<Modal
			title={data.name}
			subtitle={
				figee
					? "Campagne partie — lecture seule. Dupliquez-la pour repartir de son contenu."
					: "Composition de l'email et réglages d'envoi"
			}
			onClose={onClose}
			width={1100}
		>
			<div className="cms-editor-grid">
				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<Card>
						<CardHead title="En-tête de l'email" />
						<CardBody>
							<FormRow label="Objet" required>
								<input
									className="aform-i"
									disabled={figee || readOnly}
									value={objet}
									onChange={(e) => setObjet(e.target.value)}
								/>
							</FormRow>
							<FormRow
								label="Texte d'aperçu"
								helper="Affiché par la messagerie après l'objet"
							>
								<input
									className="aform-i"
									disabled={figee || readOnly}
									value={preheader}
									onChange={(e) => setPreheader(e.target.value)}
								/>
							</FormRow>
						</CardBody>
					</Card>

					<Card>
						<CardHead
							title="Contenu par blocs"
							sub="Variables acceptées : {{first_name}}"
						/>
						<CardBody>
							<div className="block-list">
								{blocs.map((bloc, i) => {
									const meta = BLOCK_LABELS[bloc.type];
									return (
										<div className="cms-block" key={`${bloc.type}-${i}`}>
											<div className="b-ic">
												<AdminIcon n={meta.icon} s={16} />
											</div>
											<div className="b-body">
												<div className="b-t">{meta.name}</div>
												<BlocChamps
													bloc={bloc}
													disabled={figee || readOnly}
													onChange={(p) => majBloc(i, p)}
												/>
											</div>
											{!figee && !readOnly && (
												<ActionMenu
													items={[
														{
															label: "Monter",
															icon: "upDown",
															disabled: i === 0,
															onClick: () => deplacer(i, -1),
														},
														{
															label: "Descendre",
															icon: "upDown",
															disabled: i === blocs.length - 1,
															onClick: () => deplacer(i, 1),
														},
														"sep",
														{
															label: "Retirer",
															icon: "close",
															danger: true,
															onClick: () => retirer(i),
														},
													]}
												/>
											)}
										</div>
									);
								})}
								{blocs.length === 0 && (
									<div className="empty">
										Email vide. Ajoutez un bloc depuis la liste ci-dessous.
									</div>
								)}
							</div>

							{!figee && !readOnly && (
								<>
									<div
										className="muted"
										style={{
											fontSize: 10,
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.08em",
											margin: "14px 0 8px",
										}}
									>
										Ajouter un bloc
									</div>
									<div
										style={{
											display: "grid",
											gridTemplateColumns:
												"repeat(auto-fit, minmax(min(150px, 100%), 1fr))",
											gap: 6,
										}}
									>
										{BLOCK_TYPES.map((t) => (
											<button
												key={t}
												type="button"
												className="palette-item"
												onClick={() => ajouter(t)}
												title={BLOCK_LABELS[t].desc}
											>
												<AdminIcon n={BLOCK_LABELS[t].icon} s={13} />
												{BLOCK_LABELS[t].name}
											</button>
										))}
									</div>
								</>
							)}
						</CardBody>
					</Card>
				</div>

				<div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
					<Card style={{ padding: 14 }}>
						<div
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
								marginBottom: 10,
							}}
						>
							<span
								className="muted"
								style={{
									fontSize: 10,
									fontWeight: 700,
									textTransform: "uppercase",
									letterSpacing: "0.08em",
								}}
							>
								Aperçu
							</span>
							{/* Un email est bâti à 600 px de large et ne se remet pas en page :
							    proposer « mobile » et « ordinateur » ne montrerait donc rien de
							    différent. Ce qui manque vraiment, c'est de pouvoir le lire. */}
							<span style={{ marginLeft: "auto" }}>
								<AdminButton
									variant="ghost"
									size="sm"
									onClick={() => setApercuGrand(true)}
								>
									<AdminIcon n="eye" s={12} />
									Lire en grand
								</AdminButton>
							</span>
						</div>
						<div className="email-preview">
							<div className="ep-bar">
								De : Akwaba · Objet : {objet || "(sans objet)"}
							</div>
							{/*
							  Rendu dans un cadre isolé : le HTML d'email porte ses propres
							  styles en ligne, qui déborderaient sur l'admin autrement.

							  `allow-same-origin` et rien d'autre : sans cette permission, le
							  document n'est pas peint du tout dans un cadre déjà imbriqué.
							  Les scripts restent bloqués — ils exigeraient `allow-scripts`,
							  qui n'est pas accordé — tout comme l'envoi de formulaire et la
							  navigation de la page parente.

							  Le cadre fait toujours 600 px — la largeur sur laquelle les
							  emails sont construits — et c'est la mise à l'échelle qui le
							  réduit. Le rétrécir vraiment couperait la moitié droite du
							  message, exactement ce qu'un aperçu doit éviter.
							*/}
							<div className="ep-scale">
								<iframe
									className="ep-frame"
									title="Aperçu de l'email"
									sandbox="allow-same-origin"
									srcDoc={apercu?.html ?? ""}
								/>
							</div>
						</div>
					</Card>

					<Card>
						<CardHead title="Envoi" />
						<CardBody>
							<FormRow label="Audience">
								<select
									className="aform-i"
									disabled={figee || readOnly}
									value={segment}
									onChange={(e) => setSegment(e.target.value)}
								>
									<option value="">Tous les abonnés opt-in</option>
									{(segments ?? []).map((s) => (
										<option key={s.id} value={s.id}>
											{s.name}
										</option>
									))}
								</select>
							</FormRow>
							<FormRow
								label="Date et heure d'envoi"
								helper="Vide = tout de suite"
							>
								<input
									type="datetime-local"
									className="aform-i"
									disabled={figee || readOnly}
									value={quand}
									onChange={(e) => setQuand(e.target.value)}
								/>
							</FormRow>

							<div className="info-row">
								<span className="l">Destinataires joignables</span>
								<span className="v tn">
									{estimation ? nombre(estimation.recipients) : "…"}
								</span>
							</div>
							<div className="info-row" style={{ borderBottom: "none" }}>
								<span className="l">Validation requise</span>
								<span className="v">
									{estimation?.approvalRequired ? "Oui" : "Non"}
								</span>
							</div>

							{estimation?.approvalRequired && (
								<p
									style={{
										fontSize: 10.5,
										color: "var(--a-warning)",
										marginTop: 8,
										lineHeight: 1.5,
									}}
								>
									Au-delà de 5 000 destinataires, l'envoi attend la validation
									d'un responsable : une erreur de ciblage à cette échelle abîme
									la réputation du domaine pour des semaines.
								</p>
							)}

							{!figee && !readOnly && (
								<>
									<div style={{ display: "flex", gap: 6, marginTop: 12 }}>
										<input
											className="aform-i"
											placeholder="moi@akwaba.cm"
											value={emailTest}
											onChange={(e) => setEmailTest(e.target.value)}
										/>
										<AdminButton
											variant="ghost"
											disabled={!emailTest.includes("@") || test.isPending}
											onClick={() => test.mutate()}
										>
											Test
										</AdminButton>
									</div>
									<AdminButton
										variant="secondary"
										style={{ width: "100%", marginTop: 8 }}
										disabled={enregistrer.isPending}
										onClick={() => enregistrer.mutate()}
									>
										Enregistrer
									</AdminButton>
									<AdminButton
										variant="primary"
										style={{ width: "100%", marginTop: 8 }}
										disabled={blocs.length === 0 || planifier.isPending}
										onClick={() => {
											void confirm({
												title: quand
													? "Programmer cet envoi ?"
													: "Envoyer maintenant ?",
												message: `${nombre(estimation?.recipients ?? 0)} destinataire(s)${
													quand ? ` · ${dateHeure(new Date(quand).toISOString())}` : ""
												}. L'audience sera figée à cet instant.`,
												confirmLabel: quand ? "Programmer" : "Envoyer",
												tone: "default",
											}).then((ok) => {
												if (ok) planifier.mutate();
											});
										}}
									>
										<AdminIcon n="check" s={14} />
										{quand ? "Programmer l'envoi" : "Envoyer maintenant"}
									</AdminButton>
								</>
							)}
						</CardBody>
					</Card>
				</div>
			</div>

			<ModalFooter>
				<AdminButton variant="ghost" onClick={onClose}>
					Fermer
				</AdminButton>
			</ModalFooter>

			{apercuGrand && (
				<Modal
					title="Aperçu de l'email"
					subtitle={objet || "(sans objet)"}
					onClose={() => setApercuGrand(false)}
					width={700}
				>
					<div className="email-preview wide">
						<div className="ep-scale">
							<iframe
								className="ep-frame"
								title="Aperçu de l'email en taille réelle"
								sandbox="allow-same-origin"
								srcDoc={apercu?.html ?? ""}
							/>
						</div>
					</div>
					<ModalFooter>
						<AdminButton
							variant="primary"
							onClick={() => setApercuGrand(false)}
						>
							Fermer
						</AdminButton>
					</ModalFooter>
				</Modal>
			)}
		</Modal>
	);
}

/** Champs d'un bloc, propres à son type. */
function BlocChamps({
	bloc,
	disabled,
	onChange,
}: {
	bloc: NewsletterBlock;
	disabled: boolean;
	onChange: (p: Partial<NewsletterBlock>) => void;
}) {
	const champ = (
		cle: "title" | "body" | "url" | "code",
		placeholder: string,
		multi = false,
	) =>
		multi ? (
			<textarea
				className="aform-i"
				rows={2}
				disabled={disabled}
				placeholder={placeholder}
				value={bloc[cle] ?? ""}
				onChange={(e) => onChange({ [cle]: e.target.value })}
				style={{ marginTop: 4 }}
			/>
		) : (
			<input
				className="aform-i"
				disabled={disabled}
				placeholder={placeholder}
				value={bloc[cle] ?? ""}
				onChange={(e) => onChange({ [cle]: e.target.value })}
				style={{ marginTop: 4 }}
			/>
		);

	switch (bloc.type) {
		case "header":
			return champ("title", "Bonjour {{first_name}} 👋");
		case "text":
			return champ("body", "Votre texte…", true);
		case "banner":
			return (
				<>
					{champ("title", "Titre de l'offre")}
					{champ("code", "Code promo (facultatif)")}
				</>
			);
		case "review":
			return (
				<>
					{champ("body", "« Livraison rapide, produit conforme »", true)}
					{champ("title", "Signature — Aïcha, Douala")}
				</>
			);
		case "article":
			return (
				<>
					{champ("title", "Titre de l'article")}
					{champ("url", "Adresse de l'article")}
				</>
			);
		case "button":
			return (
				<>
					{champ("title", "Texte du bouton")}
					{champ("url", "Où il mène")}
				</>
			);
		case "products":
			return (
				<>
					{champ("title", "Titre de la sélection")}
					<input
						className="aform-i"
						disabled={disabled}
						placeholder="Identifiants des produits, séparés par des virgules"
						value={(bloc.productIds ?? []).join(", ")}
						onChange={(e) =>
							onChange({
								productIds: e.target.value
									.split(",")
									.map((x) => x.trim())
									.filter(Boolean),
							})
						}
						style={{ marginTop: 4 }}
					/>
				</>
			);
		default:
			return null;
	}
}

/** Rapport d'une campagne envoyée. */
function RapportModal({ id, onClose }: { id: string; onClose: () => void }) {
	const { data, isLoading } = useQuery({
		queryKey: [...CLE, "report", id],
		queryFn: () => newslettersApi.report(id),
	});

	return (
		<Modal
			title="Rapport de campagne"
			subtitle={data?.campaign.name}
			onClose={onClose}
			width={560}
		>
			{isLoading || !data ? (
				<div className="empty">Chargement…</div>
			) : (
				<>
					<div
						className="kpi-grid"
						style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
					>
						<div className="kpi">
							<div className="kpi-l">Envoyés</div>
							<div className="kpi-v tn">{nombre(data.sent)}</div>
						</div>
						<div className="kpi accent">
							<div className="kpi-l">Ouvertures</div>
							<div className="kpi-v tn">{nombre(data.opened)}</div>
							<span className="kpi-delta accent">
								{data.openRate.toFixed(1)} %
							</span>
						</div>
						<div className="kpi forest">
							<div className="kpi-l">Clics</div>
							<div className="kpi-v tn">{nombre(data.clicked)}</div>
							<span className="kpi-delta forest">
								{data.clickRate.toFixed(1)} %
							</span>
						</div>
						<div className="kpi">
							<div className="kpi-l">Échecs</div>
							<div className="kpi-v tn">{nombre(data.failed)}</div>
						</div>
					</div>
					<p
						className="muted"
						style={{ fontSize: 11, marginTop: 10, lineHeight: 1.5 }}
					>
						Les ouvertures sont comptées une fois par destinataire. Un client
						qui bloque les images n'est compté qu'au premier clic.
					</p>
				</>
			)}
			<ModalFooter>
				<AdminButton variant="primary" onClick={onClose}>
					Fermer
				</AdminButton>
			</ModalFooter>
		</Modal>
	);
}
